<template>
  <div 
    class="flex flex-col relative select-none w-full"
    :class="[
      align === 'center' ? 'items-center' : 'items-start',
    ]"
    @touchmove="onTouch"
    ref="$container"
  >
    <div class="grow flex"
      :style="{
        width: width + 'px',
        margin: align === 'center' ? `0 -${spacing}px` : ''
      }"
      :class="[ isMoving ? 'cursor-grabbing' : 'cursor-grab' ]"
    >
      <TransitionGroup :name="transitionGroup" tag="div" class="flex items-center" :class="[ isMoving ? 'pointer-events-none' : '']" :style="{ transform: `translateX(${-position}px)`, transitionTimingFunction: transition, transitionDuration: (isDragging ? '0ms' : transitionDuration + 'ms') }" ref="$slider">
        <template v-for="(slot, i) of Object.keys($slots).filter(a => !hidden.includes(a)).sort((a, b) => order.indexOf(a) - order.indexOf(b))" :key="slot">
          <div
            class="shrink-0 transition h-full"
            :style="{ width: width + 'px', padding: `0 ${spacing}px` }"
            :class="[
              effect === 'zoom' ? (i == activeItem ? '' : i == preSelected ? 'scale-95' : 'scale-90 opacity-50') : '',
              effect === 'opacity' ? (i == activeItem ? '' : i == preSelected ? 'opacity-50' : 'opacity-30') : '',
            ]"
            @click="() => onSelect(i)"
          >
            <slot :name="slot" :is-slot-active="i >= modelValue - activeOffset && i <= modelValue + activeOffset" :is-slot-current="i === modelValue" />
          </div>
        </template>
      </TransitionGroup>
    </div>
    
    <div
      class="flex justify-center"
      :style="{
        width: align === 'center' ? width + 'px' : '100%',
        padding: align === 'center' && pagination !== 'full' ? '0 32px' : `0 ${spacing}px`,
        ...(paginationSpacing ? { marginTop: paginationSpacing + 'rem' } : {})
      }"
      :class="[
        pagination === 'full' ? 'mt-4' : 'mt-6',
        pagination === 'dots' ? 'space-x-1.5' : 'space-x-1',
      ]"
      v-if="Object.keys($slots).length > 1 && pagination !== 'none'"
    >
      <div
        class="rounded-full cursor-pointer"
        v-for="i in Object.keys($slots).length"
        :class="[
          pagination === 'dots' ? 'h-2 w-2' : 'flex-1 h-1',
          i - 1 === activeItem ? '' : (i - 1 === preSelected && isDragging) ? 'opacity-30' : 'opacity-30',
          align === 'center' && pagination !== 'full' ? 'max-w-[50px]' : '',
          color === 'flamingo' ? 'bg-flamingo-400' : (
            color === 'royal' ? 'bg-royal-600' : (
              color === 'tiger' ? 'bg-tiger-500' : (
                color === 'light' ? 'bg-light-600/80 dark:bg-dark-100' : 'bg-white'
              )
            )
          )
        ]"
        :style="{
          background: theme?.base.text.accent ?? '',
        }"
        @click="activeItem = i - 1"
        :key="i"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Theme } from '@/classes'
import WindowStore from '@/store/window'

const props = withDefaults(defineProps<{
  width?: number | 'full' | 'auto'
  theme?: Theme | null
  spacing?: number
  order?: string[]
  hidden?: string[]
  modelValue?: number
  isActive?: boolean
  autoSwipe?: number
  activeOffset?: number
  color?: 'flamingo' | 'royal' | 'tiger' | 'white' | 'light'
  pagination?: 'base' | 'full' | 'dots' | 'none'
  paginationSpacing?: number
  transitionGroup?: string
  transition?: string
  transitionDuration?: number
  effect?: 'none' | 'zoom' | 'opacity'
  align?: 'center' | 'left'
}>(), {
  modelValue: 0,
  transitionGroup: '',
  transition: 'cubic-bezier(0,.32,.24,1.3)',
  transitionDuration: 300,
  order: () => [],
  hidden: () => [],
  activeOffset: 1,
  width: 300,
  isActive: true,
  spacing: 6,
  theme: null,
  autoSwipe: 0,
  pagination: 'base',
  paginationSpacing: 0,
  color: 'flamingo',
  effect: 'none',
  align: 'center'
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
}>()

const { watchersAdd, watchersStop, watchersRestart } = useWatchers()

const { modelValue } = toRefs(props)

const windowStore = WindowStore()
const { mouseX, mouseY } = storeToRefs(windowStore)

const slots = useSlots()
const $container = ref()
const $slider = ref()

const activeItem = ref()
const movement = ref(0)
const traveled = ref(0)

const { pressed: isDragging } = useMousePressed({ target: $container })
const { width: containerWidth } = useElementSize($container)
const { width: sliderWidth } = useElementSize($slider)

const elementWidth = computed(() => {
  return (sliderWidth.value / Object.keys(slots).length)
})

const isMoving = computed(() => traveled.value > 50)
const width = computed(() => props.width === 'full' ? containerWidth.value : (props.width === 'auto' && elementWidth.value > 50 ? elementWidth.value : props.width)) 

const position = computed(() => {
  const max = sliderWidth.value - containerWidth.value
  const target = (props.align === 'center' ? width.value * activeItem.value : Math.min(max, width.value * activeItem.value)) - (isMoving.value ? movement.value : 0)

  return isMoving.value || props.align === 'center' ? target : Math.max(0, Math.min(target, max))
})

const isTransition = ref(false)

const startDrag = ref({ x: 0, y: 0 })
const startDate = ref(new Date())
const multiplier = ref(0)

const preSelected = computed(() => {
  return Math.min(Math.max(0, Math.round((position.value - (width.value * 0.4 * multiplier.value)) / width.value)), Object.keys(slots).length - 1)
})

const onReset = () => {
  multiplier.value = 0

  const difference = new Date().getTime() - startDate.value.getTime()
  multiplier.value = difference > 30 && difference <= 180 ? (movement.value > 0 ? 1 : -1) : 0

  activeItem.value = multiplier.value == 0 ? preSelected.value : Math.min(Math.max(activeItem.value - 1, preSelected.value), activeItem.value + 1)

  startDrag.value = { x: 0, y: 0 }
  movement.value = 0
  traveled.value = 0

  isTransition.value = true

  setTimeout(() => {
    isTransition.value = false
  }, 300)
}

const onTouch = (e: TouchEvent) => {
  if (isMoving.value) e.preventDefault()
}

const onSelect = (i: number) => {
  if (!isMoving.value && props.isActive) activeItem.value = i
}

watchersAdd(() => watch([mouseX, isDragging], () => {
  if (!props.isActive || !isComponentActive.value) return
  
  setTimeout(() => {
    if (isDragging.value) {
      if (startDrag.value.x === 0) {
        startDrag.value = { x: mouseX.value, y: mouseY.value }
        startDate.value = new Date()
      }

      const mouvementY = Math.abs(mouseY.value - startDrag.value.y)
      movement.value = mouseX.value - startDrag.value.x

      traveled.value = Math.round(traveled.value + Math.abs(movement.value) - mouvementY)
    } else if (movement.value !== 0) {
      onReset()
    }
  })
}, { deep: true }))

watchersAdd(() => watch(modelValue, () => {
  activeItem.value = modelValue.value
}, { immediate: true }))

watchersAdd(() => watch(activeItem, () => {
  emit('update:modelValue', activeItem.value)
}))

watchersAdd(() => watch(modelValue, () => {
  isCooling.value = true
}))

watchersAdd(() => watchThrottled(mouseX, () => {
  if (!isDragging.value && startDrag.value.x !== 0 && isComponentActive.value) onReset()
}, { throttle: 50 }))

watchersAdd(() => watchDebounced(modelValue, () => {
  isCooling.value = false
}, { debounce: props.autoSwipe }))

// HOOKS

const isCooling = ref(false)

const interval = ref()

const { isComponentActive } = useKeepAlive({
  onStart: () => {
    if (props.autoSwipe) {
      let direction = 1

      if (interval.value) clearInterval(interval.value)
      interval.value = setInterval(() => {
        const max = Object.values(slots).length
        
        if (max === 1) return

        if (isCooling.value) return

        if (activeItem.value == 0) direction = 1
        if (activeItem.value == max - 1) direction = -1

        activeItem.value = activeItem.value + direction
      }, props.autoSwipe)
    }
  },
  onActivated: () => {
    watchersRestart()
  },
  onLeave: () => {
    watchersStop()

    if (interval.value) clearInterval(interval.value)
  }
})
</script>